import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import JobApplicationForm from '../JobApplicationForm';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);

  // Main navigation links
  const mainNavLinks = [
    { 
      to: '/solutions', 
      text: 'Solutions',
      dropdown: [
        { to: '/healthcare-ai', text: 'Healthcare AI' },
        { to: '/lidar-services', text: 'LiDAR & Geospatial' },
        { to: '/cloud-solutions', text: 'Cloud Solutions' },
        { to: '/software-development', text: 'Software Development' },
        { to: '/robotics', text: 'Robotics & Automation' },
        { to: '/ai-development', text: 'AI Development' }
      ]
    },
    { to: '/services', text: 'Services' },
    { to: '/industries', text: 'Industries' },
    { to: '/about', text: 'Company' },
    { to: '/contact', text: 'Contact' }, 
  ];

  return (
    <header className="bg-figma-dark-bg-secondary/80 backdrop-blur-md sticky top-0 z-50 w-full border-b border-figma-dark-border/50 shadow-lg">
      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2 text-2xl font-bold text-figma-dark-text-primary hover:text-figma-dark-primary transition-colors">
              <img className="h-8 w-auto" src="/assets/logo.png" alt="Your Company Logo" /> 
              {/* Optional: Add text logo if image fails or for style */}
              {/* <span className="hidden sm:inline">YourCompany</span> */}
            </Link>
          </div>

          {/* Desktop Main Nav Links */}
          <nav className="hidden md:flex space-x-1 lg:space-x-2 items-center">
            {mainNavLinks.map((link) => (
              <div key={link.text} className="relative group">
                {link.dropdown ? (
                  <button
                    onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center
                      ${isSolutionsOpen 
                        ? 'text-figma-dark-primary bg-figma-dark-primary/10' 
                        : 'text-figma-dark-text-secondary hover:text-figma-dark-text-primary hover:bg-figma-dark-bg-tertiary/70'
                      }`}
                  >
                    {link.text}
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </button>
                ) : (
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-md text-sm font-medium transition-colors
                      ${isActive 
                        ? 'text-figma-dark-primary bg-figma-dark-primary/10' 
                        : 'text-figma-dark-text-secondary hover:text-figma-dark-text-primary hover:bg-figma-dark-bg-tertiary/70'
                      }`
                    }
                  >
                    {link.text}
                  </NavLink>
                )}
                
                {/* Dropdown Menu */}
                {link.dropdown && isSolutionsOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-figma-dark-bg-secondary border border-figma-dark-border">
                    <div className="py-1">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.text}
                          to={item.to}
                          className="block px-4 py-2 text-sm text-figma-dark-text-secondary hover:bg-figma-dark-bg-tertiary/70 hover:text-figma-dark-text-primary"
                          onClick={() => setIsSolutionsOpen(false)}
                        >
                          {item.text}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>
          
          {/* Desktop Right Aligned Items - Simplified for Figma Look */}
          <div className="hidden md:flex items-center space-x-3 ml-4">
            <Link to="/contact" className="px-5 py-2.5 text-sm font-medium text-figma-dark-text-primary bg-figma-dark-primary hover:bg-figma-dark-primary-hover rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-105">
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="-mr-2 flex md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-figma-dark-text-secondary hover:text-figma-dark-primary hover:bg-figma-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-figma-dark-primary"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Open main menu</span>
              {!isOpen ? (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-figma-dark-bg-secondary/95 backdrop-blur-md border-b border-figma-dark-border/50 absolute w-full shadow-xl" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {mainNavLinks.map((link) => (
              <div key={link.text}>
                {link.dropdown ? (
                  <>
                    <button
                      onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                      className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-figma-dark-text-secondary hover:bg-figma-dark-bg-tertiary/70 hover:text-figma-dark-text-primary"
                    >
                      {link.text}
                    </button>
                    {isSolutionsOpen && (
                      <div className="pl-4 mt-1 space-y-1">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.text}
                            to={item.to}
                            onClick={() => {
                              setIsOpen(false);
                              setIsSolutionsOpen(false);
                            }}
                            className="block px-3 py-2 rounded-md text-base font-medium text-figma-dark-text-secondary hover:bg-figma-dark-bg-tertiary/70 hover:text-figma-dark-text-primary"
                          >
                            {item.text}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={link.to}
                    onClick={() => setIsOpen(false)}
                    className={({ isActive }) =>
                      `block px-3 py-2 rounded-md text-base font-medium transition-colors
                      ${isActive ? 'bg-figma-dark-primary/10 text-figma-dark-primary' : 'text-figma-dark-text-secondary hover:bg-figma-dark-bg-tertiary/70 hover:text-figma-dark-text-primary'}`
                    }
                  >
                    {link.text}
                  </NavLink>
                )}
              </div>
            ))}
            {/* Mobile Auth Links */}
            <div className="pt-4 mt-4 border-t border-figma-dark-border/50">
                <Link 
                    to="/contact" 
                    onClick={() => setIsOpen(false)}
                    className="mt-1 block w-full text-left px-3 py-3 rounded-md text-base font-medium text-figma-dark-text-primary bg-figma-dark-primary hover:bg-figma-dark-primary-hover transition-colors"
                >
                    Get Started
                </Link>
            </div>
          </div>
        </div>
      )}

      {/* Job Application Form Modal */}
      {showJobForm && <JobApplicationForm onClose={() => setShowJobForm(false)} />}
    </header>
  );
};

export default Navbar; 